// Gebeurtenissen tijdens een shift: een regenbui, mist of een marktdag die een straat afsluit.
// Elke gebeurtenis wordt eerst aangekondigd (je hebt even de tijd om je route aan te passen), dan actief,
// en daarna weer voorbij. Pure logica: Shift houdt de klok bij, BezorgerGame past het weer en de stad aan.
import { t } from '../i18n';
import type { Rect } from './route';
import type { Rng } from './orders';

export type EventKind = 'bui' | 'mist' | 'markt';
export type EventPhase = 'wacht' | 'gewaarschuwd' | 'bezig' | 'voorbij';

/** Een straat die tijdens een marktdag dichtgaat. */
export interface Closure extends Rect {
  id: string;
  /** Loopt de straat horizontaal ('h') of verticaal ('v')? */
  axis: 'h' | 'v';
  /** Naam van de straat, voor in de melding */
  street: string;
  name: string;
}

/**
 * Stukken weg die dicht kunnen. Elk stuk ligt tussen twee kruispunten, zonder klanten, wegwerkzaamheden
 * of de gracht erin (gecontroleerd tegen de echte kaart), zodat er altijd een omweg bestaat.
 */
export const CLOSURES: readonly Closure[] = [
  { id: 'noorderlaan', axis: 'h', street: 'Noorderlaan', get name() { return t('event.markt.name', { street: this.street }); }, x: 3160, y: 350, w: 180, h: 140 },
  { id: 'singelweg', axis: 'h', street: 'Singelweg', get name() { return t('event.markt.name', { street: this.street }); }, x: 2950, y: 1850, w: 180, h: 140 },
  { id: 'zuiderboulevard', axis: 'h', street: 'Zuiderboulevard', get name() { return t('event.markt.name', { street: this.street }); }, x: 1450, y: 2650, w: 180, h: 180 },
  { id: 'hoofdstraat', axis: 'h', street: 'Hoofdstraat', get name() { return t('event.markt.name', { street: this.street }); }, x: 3200, y: 1100, w: 180, h: 200 },
  { id: 'industrieweg', axis: 'h', street: 'Industrieweg', get name() { return t('event.markt.name', { street: this.street }); }, x: 1500, y: 3450, w: 180, h: 140 },
  { id: 'havenring', axis: 'h', street: 'Havenring Zuid', get name() { return t('event.markt.name', { street: this.street }); }, x: 2000, y: 4250, w: 180, h: 140 },
];

export interface GameEvent {
  id: string;
  kind: EventKind;
  /** Shifttijd (s) waarop de waarschuwing komt */
  warnAt: number;
  startsAt: number;
  endsAt: number;
  closure?: Closure;
  phase: EventPhase;
}

export interface EventNotice {
  type: 'waarschuwing' | 'start' | 'einde';
  event: GameEvent;
}

/** Zoveel seconden vooraf krijg je te horen dat er iets aankomt. */
export const EVENT_WARNING_SEC = 12;

export const EVENT_DURATION: Record<EventKind, number> = { bui: 40, mist: 45, markt: 50 };

/**
 * Tijdvakken (s) waarin een gebeurtenis kan beginnen, per aantal gebeurtenissen. De langste gebeurtenis
 * duurt 50 seconden; elk vak eindigt dus ruim voor het begin van het volgende en alles past binnen de
 * 240 seconden van een shift.
 */
const SLOTS: Record<number, [number, number][]> = {
  2: [[40, 65], [130, 150]],
  3: [[25, 40], [100, 120], [180, 185]],
};

/**
 * Plant de gebeurtenissen voor een shift. Rang 1 krijgt er twee (eerst weer, dan een marktdag),
 * rang 2 twee willekeurige, rang 3 drie. Hetzelfde rng geeft dezelfde gebeurtenissen (dagelijkse uitdaging).
 */
export function planEvents(rng: Rng, rank: number): GameEvent[] {
  const count = rank >= 3 ? 3 : 2;
  const kinds: EventKind[] = [];
  const pick = (): EventKind => {
    const r = rng();
    return r < 0.34 ? 'bui' : r < 0.55 ? 'mist' : 'markt';
  };
  for (let i = 0; i < count; i++) {
    let k: EventKind;
    if (rank === 1 && i === 0) k = rng() < 0.7 ? 'bui' : 'mist'; // eerst iets rustigs
    else if (rank === 1 && i === 1) k = 'markt';
    else {
      do k = pick();
      while (k === kinds[i - 1]); // nooit twee keer hetzelfde achter elkaar
    }
    kinds.push(k);
  }

  const usedClosures = new Set<string>();
  return kinds.map((kind, i) => {
    const [from, to] = SLOTS[count][i];
    const startsAt = Math.round(from + rng() * (to - from));
    let closure: Closure | undefined;
    if (kind === 'markt') {
      const free = CLOSURES.filter((c) => !usedClosures.has(c.id));
      closure = free[Math.floor(rng() * free.length)];
      usedClosures.add(closure.id);
    }
    return {
      id: `e${i + 1}`,
      kind,
      warnAt: startsAt - EVENT_WARNING_SEC,
      startsAt,
      endsAt: startsAt + EVENT_DURATION[kind],
      closure,
      phase: 'wacht',
    };
  });
}

/** Werkt de fase van elke gebeurtenis bij voor tijdstip t en geeft de meldingen terug die erbij horen. */
export function advanceEvents(events: GameEvent[], t: number): EventNotice[] {
  const notices: EventNotice[] = [];
  for (const e of events) {
    if (e.phase === 'wacht' && t >= e.warnAt) {
      e.phase = 'gewaarschuwd';
      notices.push({ type: 'waarschuwing', event: e });
    }
    if (e.phase === 'gewaarschuwd' && t >= e.startsAt) {
      e.phase = 'bezig';
      notices.push({ type: 'start', event: e });
    }
    if (e.phase === 'bezig' && t >= e.endsAt) {
      e.phase = 'voorbij';
      notices.push({ type: 'einde', event: e });
    }
  }
  return notices;
}

export const activeEvent = (events: readonly GameEvent[], kind?: EventKind) =>
  events.find((e) => e.phase === 'bezig' && (!kind || e.kind === kind)) ?? null;

/** De afgesloten straten op dit moment. */
export const activeClosures = (events: readonly GameEvent[]): Closure[] =>
  events.filter((e) => e.phase === 'bezig' && e.closure).map((e) => e.closure!);

export type ShiftWeather = 'sunny' | 'rain' | 'mist';

/** Het weer volgt de gebeurtenissen: bui is regen, mist is mist, anders zon. */
export function weatherFor(events: readonly GameEvent[]): ShiftWeather {
  if (activeEvent(events, 'bui')) return 'rain';
  if (activeEvent(events, 'mist')) return 'mist';
  return 'sunny';
}

export const EVENT_TEXT: Record<EventKind, { icon: string; warn: (e: GameEvent) => string; start: (e: GameEvent) => string; readonly end: string }> = {
  bui: {
    icon: '🌧️',
    warn: () => t('event.bui.warn', { sec: EVENT_WARNING_SEC }),
    start: () => t('event.bui.start'),
    get end() { return t('event.bui.end'); },
  },
  mist: {
    icon: '🌫️',
    warn: () => t('event.mist.warn', { sec: EVENT_WARNING_SEC }),
    start: () => t('event.mist.start'),
    get end() { return t('event.mist.end'); },
  },
  markt: {
    icon: '🛒',
    warn: (e) => t('event.markt.warn', { sec: EVENT_WARNING_SEC, street: e.closure?.street ?? '' }),
    start: (e) => t('event.markt.start', { street: e.closure?.street ?? '' }),
    get end() { return t('event.markt.end'); },
  },
};
